import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../../store';
import { BadgeCheck, ArrowRight, Eye, EyeOff, PlusCircle, Users } from 'lucide-react';
import api from '../../../../api/axios';
import { toggleEarningsVisibility } from '../../../../store/slices/authSlice';
import { useCurrency } from '../../../../hooks/useCurrency';
import { Button, IconButton } from '@mui/material';

interface UserDetailsForProfileDTO {
  seller: boolean;
  earning: {
    totalEarningsInMinor: number;
    totalPurchasesInMinor: number;
  };
}

interface UserDetailsProps {
  onLoaded: () => void;
}

const UserDetails = ({ onLoaded }: UserDetailsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formatAmount } = useCurrency();

  // Redux state
  const auth = useSelector((state: RootState) => state.auth);
  const { symbol } = useSelector((state: RootState) => state.currency);
  const isHidden = useSelector((state: RootState) => state.auth.isEarningsHidden);

  const selectedAccount = auth.selectedSteamAccount;
  const accountCount = auth.steamAccounts.length;
  const isFullyVerified = auth.kycStatus === 'APPROVED';

  const [details, setDetails] = useState<UserDetailsForProfileDTO | null>(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await api.get<UserDetailsForProfileDTO>('/profile/user-details');
        setDetails(response.data);
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        onLoaded();
      }
    };
    fetchUserDetails();
  }, [onLoaded]);

  if (!details) return null;

  const netEarnings = details.earning.totalEarningsInMinor - details.earning.totalPurchasesInMinor;

  // Helper to render masked or formatted values
  const renderValue = (minorValue: number) => {
    if (isHidden) return '••••••';
    return `${symbol} ${formatAmount(minorValue)}`;
  };

  return (
      <section className="max-w-[1600px] mx-auto px-4 md:px-6 py-2 font-sans text-light-text dark:text-dark-text">
        <div className="bg-neutral-50 dark:bg-neutral-900/20 border border-neutral-200 dark:border-white/5 rounded-xl shadow-xl p-5 md:p-6 backdrop-blur-md flex flex-col md:flex-row justify-between items-stretch gap-6">

          {/* User Info Left */}
          <div className="flex flex-col justify-between items-start flex-1 min-h-[130px]">
            <div className="flex items-start gap-5">
              <div className="relative">
                <img
                    src={selectedAccount?.avatarUrl || 'https://via.placeholder.com/64'}
                    alt="Avatar"
                    className="w-16 h-16 rounded-xl border border-white/10 shadow-lg object-cover"
                />
                {isFullyVerified && (
                    <div className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white p-0.5 rounded-full shadow-lg">
                      <BadgeCheck size={14} />
                    </div>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h2 className="text-xl font-black tracking-tight leading-none">
                    {selectedAccount?.profileName || 'Unknown User'}
                  </h2>
                  <div className="flex gap-1.5">
                  <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-wider border ${
                      isFullyVerified
                          ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                          : 'bg-neutral-200/50 dark:bg-white/5 text-gray-500 dark:text-slate-400 border-neutral-300 dark:border-white/5'
                  }`}>
                    {isFullyVerified ? t('dashboard.profile.user_details.verified') : t('dashboard.profile.user_details.unverified')}
                  </span>
                    {details.seller && (
                        <span className="px-2 py-0.5 rounded-lg bg-emerald-500/10 text-emerald-500 text-[9px] font-black uppercase tracking-wider border border-emerald-500/20">
                      {t('dashboard.profile.user_details.seller')}
                    </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-gray-400 dark:text-slate-500">
                  <Users size={12} />
                  <span className="text-[11px] font-bold">
                  {accountCount} {accountCount === 1 ? t('dashboard.profile.user_details.account_single') : t('dashboard.profile.user_details.account_plural')}
                </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              {!isFullyVerified && (
                  <Button
                      variant="contained"
                      color="inherit"
                      onClick={() => navigate('/profile/kyc')}
                      className="shadow-sm font-bold text-[11px]"
                  >
                    {t('dashboard.profile.user_details.complete_kyc')}
                  </Button>
              )}

              <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/profile/connect-account')}
                  className="group font-bold text-[11px]"
              >
                <PlusCircle size={14} className="mr-2" />
                {t('dashboard.profile.user_details.add_account')}
                <ArrowRight size={12} className="ml-1.5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          {/* Earnings Card Right */}
          <div className="w-full md:w-[340px] bg-white dark:bg-neutral-800/40 border border-neutral-200 dark:border-white/5 rounded-xl p-4 shadow-sm self-center">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-2">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500">
                  {t('dashboard.profile.user_details.earnings_title')}
                </h4>
                <IconButton
                    size="medium"
                    onClick={() => dispatch(toggleEarningsVisibility())}
                    sx={{ color: 'gray' }}
                >
                  {isHidden ? <EyeOff size={14} /> : <Eye size={14} />}
                </IconButton>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center text-[13px]">
                <span className="font-bold text-gray-500 dark:text-slate-400">{t('dashboard.profile.user_details.sales')}</span>
                <span className="font-black font-mono">
                {renderValue(details.earning.totalEarningsInMinor)}
              </span>
              </div>

              <div className="flex justify-between items-center text-[13px]">
                <span className="font-bold text-gray-500 dark:text-slate-400">{t('dashboard.profile.user_details.purchases')}</span>
                <span className="font-black font-mono">
                {renderValue(details.earning.totalPurchasesInMinor)}
              </span>
              </div>

              <div className="pt-1.5 mt-1.5 border-t border-neutral-100 dark:border-white/5 flex justify-between items-center">
                <span className="text-[10px] font-black uppercase text-gray-400 dark:text-slate-500">{t('dashboard.profile.user_details.net')}</span>
                <span className={`text-base font-black font-mono ${!isHidden && netEarnings < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {renderValue(netEarnings)}
              </span>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
};

export default UserDetails;